/**
 * URL scheme helpers for free-text URL fields (e.g. a lead's `website`).
 * Backend validation (`sales_leads_module.actions._validation.validate_website`)
 * rejects anything but http(s) at write time, but existing data or a
 * defence-in-depth render check still needs a client-side guard before
 * turning a stored value into a clickable `<a href>`.
 */

/** True only for a value that parses as an http:// or https:// URL. */
export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * The value as a path inside this app, or null. A server-supplied "go here" address (an order's follow URL) is
 * trusted only as a single-slash relative path: anything absolute (`https://...`), protocol-relative (`//host`),
 * carrying a colon anywhere (a scheme), a backslash (some browsers read `/\host` as `//host`) or a control
 * character is refused, so a bad value can never send the person off the site.
 */
export function sameAppPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  // eslint-disable-next-line no-control-regex
  if (/[:\\\u0000-\u001f\u007f]/.test(value)) return null;
  return value;
}

/**
 * Prefixes a bare host/domain (e.g. "hepcorail.co.uk") with "https://" so
 * it round-trips as a deliverable link instead of a same-origin relative
 * path. Leaves an already-schemed value untouched.
 */
export function withHttpScheme(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  // Match on the colon alone, not `://` — a value already carrying *any*
  // scheme (including a dangerous one like `javascript:`) must pass through
  // untouched so the backend's validate_website sees and rejects it with
  // the proper FieldError, rather than this helper prefixing it into
  // "https://javascript:alert(1)", which validate_website's own
  // first-`://`-partition then reads as a (bogus but accepted) https URL.
  return isHttpUrl(trimmed) || /^[a-z][a-z0-9+.-]*:/i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
}
